import { getSourceKey, type NumberedCitation } from "#features/citations/citedContent.ts";
import { CitationPill } from "#features/conversations/components/conversation-source/CitationPill.tsx";
import { joinSourceMeta, useCitationSourceLabels } from "#features/conversations/components/conversation-source/citationSource.tsx";
import { useConversationSource } from "#features/conversations/hooks/useConversationSource.ts";
import { useHoveredSource } from "#features/conversations/hooks/useHoveredSource.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent, MixpanelLinkType, MixpanelSourceLocation } from "#types/tracking.ts";
import type { WebPageCustomProperties } from "#types/types.ts";
import { Link } from "@hopper-ui/components";
import { IconOpenLink } from "@intentui/icons";
import { useCallback } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { useParams } from "react-router";
import "#css/ink.css";
import "./SourcesSidePanelCard.css";

// The preview is plain text over two lines: paragraphs run inline as spans (the row is phrasing
// content), any other markup unwraps.
const PREVIEW_ELEMENTS = ["p"];
const PREVIEW_COMPONENTS: Components = {
    p: ({ children }) => <span className="wai-sources-row-paragraph">{children}</span>
};

// The list reveals row by row; past the tenth row the rows come in together, so a long list shows at once.
export const SOURCES_MAX_STAGGER_DELAY = 600;

interface Props {
    citation: NumberedCitation;
}

/** A row of the sources list: the source's numbered pill, its title, "App · Type · Date" and a two-line preview. */
export const SourcesSidePanelCard = ({ citation: { source, displayNumber } }: Props) => {
    const { conversationId } = useParams<{ conversationId: string }>();
    const { isHovered, onMouseEnter, onMouseLeave } = useHoveredSource(getSourceKey(source));
    const { title, date, description } = useConversationSource(source);
    const { appName, typeLabel } = useCitationSourceLabels(source);
    const { trackEvent } = useTracking();

    // Validate URL - handles null and undefined
    const hasValidUrl = Boolean(source.url);
    // A web page names its site rather than the crawler.
    const siteName = source.sourceType === "web_page" ? (source.customProperties as WebPageCustomProperties | undefined)?.rootUrlFriendlyName : null;
    // A table's preview is the table itself, which two lines of text cannot show.
    const preview = source.sourceType === "table" ? "" : description;

    const onClick = useCallback((event: React.MouseEvent) => {
        // If source url is not available, prevent default link behavior
        if (!hasValidUrl) {
            event.preventDefault();
            event.stopPropagation();

            return; // Early return, no tracking for invalid URLs
        }

        trackEvent(MixpanelEvent.AssistantSourceClicked, {
            Location: MixpanelSourceLocation.List,
            "Conversation Id": conversationId,
            Type: MixpanelLinkType.Source
        });
    }, [conversationId, hasValidUrl, trackEvent]);

    return (
        <Link
            href={hasValidUrl ? source.url! : undefined}
            isExternal={hasValidUrl}
            isQuiet
            textDecoration="none"
            className="wai-sources-row"
            aria-disabled={!hasValidUrl}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            cursor={hasValidUrl ? "pointer" : "default"}
        >
            <span className="wai-sources-row-pill">
                <CitationPill source={source} displayNumber={displayNumber} isActive={isHovered} />
            </span>
            <span className="wai-sources-row-body">
                <span className="wai-sources-row-title">{title}</span>
                <span className="wai-sources-row-meta">{joinSourceMeta(siteName || appName, typeLabel, date)}</span>
                {preview &&
                    <span className="wai-sources-row-preview">
                        <ReactMarkdown skipHtml allowedElements={PREVIEW_ELEMENTS} unwrapDisallowed components={PREVIEW_COMPONENTS}>{preview}</ReactMarkdown>
                    </span>
                }
            </span>
            {hasValidUrl && <IconOpenLink className="wai-sources-row-open" aria-hidden="true" />}
        </Link>
    );
};
